import { Component, OnInit } from '@angular/core';
import { BreadcrumbItem } from '../../../shared-components/custom-breadcrumbs/custom-breadcrumbs.component';
import { TranslateService } from '../../../services/translate/translate.service';
import { DateLocalePipe } from '../../../services/date-locale.pipe';
import { AppContextService } from '../../../services/app-context.service';
import { User } from '../../../models/User/user';
import { ActivatedRoute } from '@angular/router';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { ConfirmingPaymentsApiService } from '../../../server-api/confirming-payments-api.service';
import { AggregateDocumentConfirmingFinancialRequest } from '../../../models/financial-movement/financial-movement';
import { RouteAuthorizationService } from '../../../services/authorization/route-authorization.service';
import { PaymentCardData } from '../../../shared-components/payment-card/payment-card.component';
import { CustomTabItem } from '../../../shared-components/custom-tabs/custom-tabs.component';

@Component({
	selector: 'eif-financial-movements',
	templateUrl: './financial-movements.component.html',
	styleUrls: ['./financial-movements.component.scss'],
	standalone: false,
})
export class FinancialMovementsComponent implements OnInit {
	public documentId: string;
	public aggregateDocument: AggregateDocumentConfirmingFinancialRequest;
	public loggedUser: User | null = null;
	public breadcrumbItems: BreadcrumbItem[] = [];
	public isPerformingRequest: boolean = false;
	public tabs: CustomTabItem[] = [];
	public activeTabId: string = 'movements';

	constructor(
		public appContext: AppContextService,
		private translateService: TranslateService,
		private route: ActivatedRoute,
		private toasterService: ToasterService,
		private confirmingPaymentsApiService: ConfirmingPaymentsApiService,
		private routeAuthorizationService: RouteAuthorizationService
	) {}

	ngOnInit(): void {
		this.initializeBreadcrumbs();
		this.tabs = this.buildTabs();
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;

			if (
				this.loggedUser?.IsBuyer ||
				this.loggedUser?.IsFinancier ||
				this.loggedUser?.IsSystemAdmin ||
				this.loggedUser?.IsSupportOperator ||
				this.loggedUser?.IsBackOfficeOperator
			) {
				// Get document ID and load document data
				this.route.params.subscribe((params) => {
					this.documentId = params['documentId'];

					if (this.documentId) {
						this.isPerformingRequest = true;
						this.confirmingPaymentsApiService.getFinancialRequestByDocumentId(this.documentId).subscribe({
							next: (result) => {
								if (result.IsValid) {
									this.aggregateDocument = result.Data;
								}
								this.isPerformingRequest = false;
							},
							error: (error) => {
								console.error('Error loading document:', error);
								this.toasterService.showError(this.translateService.translate('financial-movements.error-loading-document-data', 'client'));
								this.isPerformingRequest = false;
							},
						});
					}
				});
			} else {
				this.routeAuthorizationService.routeOnUnauthorized();
			}
		});
	}

	public get paymentCardData(): PaymentCardData {
		const datePipe = new DateLocalePipe(this.appContext);
		return {
			type: 'aggregate',
			Supplier: {
				Name: this.aggregateDocument?.Document?.Supplier?.Name,
				CountryCode: this.aggregateDocument?.Document?.Supplier?.CountryCode,
				VatNumber: this.aggregateDocument?.Document?.Supplier?.VatNumber,
			},
			Buyer: {
				Name: this.aggregateDocument?.Document?.Buyer?.Name,
				CountryCode: this.aggregateDocument?.Document?.Buyer?.CountryCode,
				VatNumber: this.aggregateDocument?.Document?.Buyer?.VatNumber,
			},
			Aggregate: {
				IssuedDate: datePipe.transform(this.aggregateDocument?.Document?.IssuedDate),
				DueDate: datePipe.transform(this.aggregateDocument?.Document?.DueDate),
			},
			DocumentsSum: this.aggregateDocument?.Document?.TotalAmount,
			DocumentsCurrency: this.aggregateDocument?.Document?.Currency,
			Labels: {
				TypeLabel: this.translateService.translate('financial-movements.type', 'client'),
				IssuedDateLabel: this.translateService.translate('financial-movements.issuedDate', 'client'),
				DueDateLabel: this.translateService.translate('financial-movements.dueDate', 'client'),
			},
		};
	}

	private buildTabs(): CustomTabItem[] {
		return [
			{ id: 'movements', label: this.translateService.translate('financial-movements.movementDetails', 'client') },
			{ id: 'registration', label: this.translateService.translate('financial-movements.registrationDetails', 'client') },
		];
	}

	public onTabSelect(tabId: string): void {
		this.activeTabId = tabId;
	}

	private initializeBreadcrumbs() {
		this.breadcrumbItems = [
			{
				label: this.translateService.translate('breadcrumb.home', 'client'),
				route: ['/private/home'],
			},
			{
				label: this.translateService.translate('breadcrumb.documents', 'client'),
				route: ['/private/documents'],
			},
			{
				label: this.translateService.translate('breadcrumb.financial-movements', 'client'),
			},
		];
	}
}
